import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MyWatchesView,
  NotifyOutcome,
  PutScopedRulesBody,
  ScopedRulesView,
  WatchState,
  WatchTargetType,
} from "@shared/types/notifications";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { invalidateNotificationDomains } from "@/hooks/notificationDomainsKey";
import { instanceHeaders } from "@/hooks/use-access";
import { apiRequest } from "@/lib/api";

/**
 * Alert routing (docs/research/2026-09-28-design-alert-routing.md): the rules for chosen
 * connections or schedules, and the caller's own Watches. Both ADD recipients to the nine rules
 * of the Notifications tab; neither removes anyone.
 *
 * Instance-scoped reads carry the selected instance in their key as well as the header, as
 * `use-notifications.ts` does, so switching instance never shows one instance's rules or
 * watches under another's name.
 */

/** Said by every Watch control while the caller's Watches could not be read. */
export const WATCH_UNAVAILABLE_MESSAGE = "Your Watches did not load. Reload the page to try again.";

/** Said by every Watch control while the caller's Watches are still loading. */
export const WATCH_LOADING_MESSAGE = "Loading your Watches…";

export const SCOPED_RULES_PATH = "/api/v2/instances/current/notifications/scoped-rules";
export const MY_WATCHES_PATH = "/api/v2/instances/current/watches";

export function scopedRulesKey(instanceId: string | null | undefined) {
  return [SCOPED_RULES_PATH, instanceId ?? null] as const;
}

export function myWatchesKey(instanceId: string | null | undefined) {
  return [MY_WATCHES_PATH, instanceId ?? null] as const;
}

/** The watch route of one connection or schedule. */
export function watchPath(targetType: WatchTargetType, targetId: string): string {
  return `/api/v2/${targetType === "dataflow" ? "dataflows" : "schedules"}/${targetId}/watch`;
}

export function useScopedRules(enabled = true) {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id;
  return useQuery<ScopedRulesView>({
    queryKey: scopedRulesKey(instanceId),
    enabled: enabled && Boolean(instanceId),
    queryFn: ({ signal }) =>
      apiRequest<ScopedRulesView>(SCOPED_RULES_PATH, {
        signal,
        headers: instanceHeaders(instanceId),
      }),
  });
}

/** A full replace; the answer is cached under the instance it names itself. */
export function useSaveScopedRules() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ instanceId, body }: { instanceId: string; body: PutScopedRulesBody }) =>
      apiRequest<ScopedRulesView>(SCOPED_RULES_PATH, {
        method: "PUT",
        body,
        // #1110: changing where alerts go needs a recent sign-in.
        stepUp: "change",
        headers: instanceHeaders(instanceId),
      }),
    onSuccess: (view) => {
      client.setQueryData(scopedRulesKey(view.instanceId), view);
      // #1111: the Allowed notification domains card counts saved addresses outside the list.
      invalidateNotificationDomains(client);
    },
  });
}

export function useMyWatches(enabled = true) {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id;
  return useQuery<MyWatchesView>({
    queryKey: myWatchesKey(instanceId),
    enabled: enabled && Boolean(instanceId),
    queryFn: ({ signal }) =>
      apiRequest<MyWatchesView>(MY_WATCHES_PATH, {
        signal,
        headers: instanceHeaders(instanceId),
      }),
  });
}

/** What `useSetWatch` sends: `outcomes` null stops watching. */
interface SetWatchVariables {
  targetType: WatchTargetType;
  targetId: string;
  /** Null stops watching. */
  outcomes: readonly NotifyOutcome[] | null;
  /** The connection's or schedule's name, shown by the optimistic row until the list is read. */
  name?: string;
}

/** The caller's Watches with one target's Watch set to `outcomes`, or removed for null. */
function withWatch(view: MyWatchesView, change: SetWatchVariables): MyWatchesView {
  const others = view.watches.filter(
    (watch) => !(watch.targetType === change.targetType && watch.targetId === change.targetId),
  );
  if (change.outcomes === null) return { ...view, watches: others };
  const existing = view.watches.find(
    (watch) => watch.targetType === change.targetType && watch.targetId === change.targetId,
  );
  return {
    ...view,
    watches: [
      ...others,
      {
        targetType: change.targetType,
        targetId: change.targetId,
        name: existing?.name ?? change.name ?? null,
        outcomes: [...change.outcomes],
        createdAt: existing?.createdAt ?? new Date().toISOString(),
      },
    ],
  };
}

/**
 * Watch (with outcomes) or stop watching one connection or schedule, for the signed-in person
 * only: the request names no user. Round 10 (C4): the caller's watch list changes at once, so a
 * badge reads its new level on the press, and goes back if the request fails; it is read again
 * after either.
 */
export function useSetWatch() {
  const client = useQueryClient();
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id;
  const key = myWatchesKey(instanceId);
  return useMutation({
    mutationFn: ({ targetType, targetId, outcomes }: SetWatchVariables) =>
      apiRequest<WatchState>(watchPath(targetType, targetId), {
        method: outcomes === null ? "DELETE" : "PUT",
        ...(outcomes === null ? {} : { body: { outcomes } }),
        headers: instanceHeaders(instanceId),
      }),
    onMutate: async (change: SetWatchVariables) => {
      // A read in flight would land after this change and put the old list back.
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<MyWatchesView>(key);
      if (previous) client.setQueryData<MyWatchesView>(key, withWatch(previous, change));
      return { previous };
    },
    onError: (_error, _change, context) => {
      if (context?.previous) client.setQueryData<MyWatchesView>(key, context.previous);
    },
    onSettled: () => client.invalidateQueries({ queryKey: key }),
  });
}
